import React from 'react';
import { Box, Drawer, List, ListItem, ListItemIcon, ListItemText, AppBar, Toolbar, Typography, alpha } from '@mui/material';
import { Dashboard, People, Work, AutoAwesome, Settings, TrendingUp } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const drawerWidth = 280;

// コンポーネント外に巻き上げ（rendering-hoist-jsx ルール適用）
const menuItems = [
  { text: 'ダッシュボード', icon: <Dashboard />, path: '/' },
  { text: 'エンジニア管理', icon: <People />, path: '/engineers' },
  { text: '案件管理', icon: <Work />, path: '/projects' },
  { text: 'マッチング結果', icon: <AutoAwesome />, path: '/matching' },
  { text: '設定', icon: <Settings />, path: '/settings' },
];

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
          color: 'text.primary',
        }}
      >
        <Toolbar>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 600,
              color: 'text.primary',
              fontSize: '1.125rem',
            }}
          >
            営業管理ダッシュボード
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mr: 2,
              color: 'text.secondary',
              fontSize: '0.875rem',
              fontWeight: 500,
            }}
          >
            最終更新: {new Date().toLocaleString('ja-JP')}
          </Typography>
        </Toolbar>
      </AppBar>

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            background: (theme) => `linear-gradient(180deg, ${theme.palette.primary.dark} 0%, #0D3A5F 100%)`,
            color: '#FFFFFF',
            borderRight: 'none',
          },
        }}
        variant="permanent"
        anchor="left"
      >
        <Box sx={{
          p: 3,
          pt: 4,
          pb: 3,
          borderBottom: '1px solid',
          borderColor: alpha('#FFFFFF', 0.1),
        }}>
          <Typography
            variant="h5"
            sx={{
              color: '#FFFFFF',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              fontSize: '1.5rem',
              letterSpacing: '-0.02em',
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                background: (theme) => `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.light} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.secondary.main, 0.3)}`,
              }}
            >
              <TrendingUp sx={{ fontSize: 20 }} />
            </Box>
            SES Matching
          </Typography>
        </Box>
        <List sx={{ px: 2, pt: 3 }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <ListItem
                key={item.text}
                onClick={() => navigate(item.path)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: (theme) => {
                    if (isActive) return alpha(theme.palette.secondary.main, 0.15);
                    return 'transparent';
                  },
                  borderRadius: '10px',
                  mb: 1,
                  px: 2,
                  py: 1.5,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  '&:hover': {
                    backgroundColor: (theme) => {
                      if (isActive) return alpha(theme.palette.secondary.main, 0.2);
                      return alpha('#FFFFFF', 0.05);
                    },
                    transform: 'translateX(4px)',
                  },
                  ...(isActive && {
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '3px',
                      height: '24px',
                      backgroundColor: (theme) => theme.palette.secondary.main,
                      borderRadius: '0 2px 2px 0',
                    }
                  })
                }}
              >
                <ListItemIcon
                  sx={{
                    color: (theme) => {
                      if (isActive) return theme.palette.secondary.main;
                      return alpha('#FFFFFF', 0.7);
                    },
                    minWidth: 40,
                    transition: 'color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    sx: {
                      fontSize: '0.9375rem',
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? '#FFFFFF' : alpha('#FFFFFF', 0.9),
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    }
                  }}
                />
              </ListItem>
            );
          })}
        </List>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: 4,
          minHeight: '100vh',
          mt: '72px',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default Layout;